import React, { useContext, useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { TemporaryChatContext } from '../../Context/temporaryChatContext';
import chatService from '../../services/chatService';
import { parsePossiblyBrokenJson } from '../../helpers/common';
import { KnowHowChatInputStyled } from './ChatSection.styles';

function extractJsonObjects(buffer) {
  const objects = [];
  let depth = 0;
  let start = 0;

  for (let i = 0; i < buffer.length; i++) {
    if (buffer[i] === '{') {
      if (depth === 0) start = i;
      depth++;
    } else if (buffer[i] === '}') {
      depth--;
      if (depth === 0) {
        objects.push(buffer.slice(start, i + 1));
      }
    }
  }

  const last = objects.length ? buffer.slice(buffer.lastIndexOf('}') + 1) : buffer;
  return { objects, leftover: last };
}

export default function KnowHowChatInput({ sessionId }) {
  const [input, setInput] = useState('');
  const [showHint, setShowHint] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef(null);
  const {
    updateUserChat,
    webSearch,
    updateBotChat,
    updateChatSource,
    selectedAgent,
    sessionAgent,
    toggleRef,
    fakeAiLoader,
    fakeAiLoaderClear,
    updateChatLoaderMessage,
    updateToolImages,
  } = useContext(TemporaryChatContext);

  const hasSession = Boolean(sessionId);
  const isDisabled = !hasSession || isSubmitting;
  const activeAgent = sessionId ? sessionAgent || selectedAgent : selectedAgent;

  const processStream = async reader => {
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const { objects, leftover } = extractJsonObjects(buffer);

      for (const jsonString of objects) {
        try {
          const parsed = JSON.parse(jsonString);
          const { type, data } = parsed;

          if (type === 'intermediate_step') {
            updateChatLoaderMessage(data);
            updateBotChat('\n \n');
          }
          if (type === 'content') updateBotChat(data);
          if (type === 'tool_image_result') updateToolImages(data);
          if (type === 'tool_result') {
            const fixed = parsePossiblyBrokenJson(data);
            updateChatSource(fixed?.search_results);
          }
        } catch (err) {
          console.warn('Failed to parse chunk:', jsonString, err);
        }
      }

      buffer = leftover;
    }

    if (buffer.trim()) {
      try {
        const parsed = JSON.parse(buffer);
        const { type, data } = parsed;
        if (type === 'intermediate_step') updateChatLoaderMessage(data);
        if (type === 'content') updateBotChat(data);
        if (type === 'tool_image_result') updateToolImages(data);
        if (type === 'tool_result') {
          const fixed = parsePossiblyBrokenJson(data);
          updateChatSource(fixed?.search_results);
        }
      } catch (err) {
        console.warn('Failed final parse:', buffer, err);
      }
    }

    updateChatLoaderMessage('');
  };

  const handleSend = async () => {
    const query = input.trim();
    if (!hasSession || !query || isSubmitting) return;

    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    setIsSubmitting(true);
    toggleRef(false);

    try {
      updateUserChat(query);
      fakeAiLoader();

      const aiResp = await chatService.createChat({
        platform_id: 'knowhowlabs',
        agent_id: activeAgent?.value,
        session_id: sessionId,
        query,
        mode: activeAgent?.mode,
        use_google_search: webSearch,
      });

      await processStream(aiResp.body.getReader());
    } catch (error) {
      fakeAiLoaderClear();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInput = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
  };

  return (
    <KnowHowChatInputStyled
      onMouseEnter={() => {
        if (!hasSession) setShowHint(true);
      }}
      onMouseLeave={() => setShowHint(false)}>
      {showHint && !hasSession && (
        <div className="session-hint">
          Click <span>New Chat</span> to start a session
        </div>
      )}
      <textarea
        ref={textareaRef}
        value={hasSession ? input : ''}
        onChange={e => {
          if (!hasSession) return;
          setInput(e.target.value);
        }}
        onInput={handleInput}
        onKeyDown={e => {
          if (!hasSession) {
            e.preventDefault();
            return;
          }
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
          }
        }}
        disabled={!hasSession}
        readOnly={!hasSession}
        placeholder={hasSession ? 'Send a message...' : 'Click New Chat to start a session...'}
        rows={1}
        title={!hasSession ? 'Click New Chat to start a session' : undefined}
        className={hasSession ? 'ready' : 'locked'}
      />
      <button
        type="button"
        onClick={handleSend}
        disabled={isDisabled || !input.trim()}
        title={!hasSession ? 'Click New Chat to start a session' : undefined}
        aria-label="Send message">
        <Send size={16} />
      </button>
    </KnowHowChatInputStyled>
  );
}
